import { ENV_CONFIG } from './env';

// API Configuration
export const API_CONFIG = {
  BASE_URL: ENV_CONFIG.API_URL,
  TIMEOUT: 30000, // 30 seconds - sufficient for paginated responses (100 items per page)
  HEADERS: {
    'Content-Type': 'application/json',
    'x-app-source': 'admin',
  },
};

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/api/v1/auth/adminLogin',
    REFRESH: '/api/v1/auth/refresh',
    LOGOUT: '/api/v1/auth/logout',
    GENERATE_RESET_OTP: '/api/v1/auth/generateResetPasswordOtp',
    VALIDATE_RESET_OTP: '/api/v1/auth/validateResetOtp',
    RESET_STUDIO_PASSWORD: '/api/v1/auth/resetStudioPassword',
  },
  
  // Users
  USERS: {
    LIST: '/api/v1/users/getAllUsers',
    SEARCH: (q: string) => `/api/v1/users/admin/search?q=${encodeURIComponent(q)}&limit=50`,
    DETAIL: (id: string) => `/api/v1/users/admin/getUserById/${id}`,
    CREATE: '/api/v1/users/admin/createUser',
    UPDATE: (id: string) => `/api/v1/users/admin/updateUser/${id}`,
    STUDIO_CREATE: '/api/v1/users/studio/createUser',
    STUDIO_UPDATE: (id: string) => `/api/v1/users/studio/updateUser/${id}`,
    DELETE: (id: string) => `/api/v1/users/admin/deleteUser/${id}`,
    BULK_TOGGLE_MARKETABLE: '/api/v1/users/admin/bulkToggleMarketable',
    STUDIO_LIST: '/api/v1/users/studio/getAllUsers',
    STUDIO_DETAIL: (id: string) => `/api/v1/users/studio/getUserById/${id}`,
    STUDIO_REMOVE: (id: string) => `/api/v1/users/studio/removeFromStudio/${id}`,
    STUDIO_MEMBERS: '/api/v1/users/studio/members',
    STUDIO_MAP: '/api/v1/users/studio/map',
    STUDIO_HEALTH_FORMS: '/api/v1/users/studio/health-forms',
  },
  
  // Studios
  STUDIOS: {
    LIST: '/api/v1/studios/admin/getAllStudios',
    DETAIL: (id: string) => `/api/v1/studios/getStudioById/${id}`,
    UPDATE: (id: string) => `/api/v1/studios/admin/editStudio/${id}`,
    UPDATE_STUDIO: '/api/v1/studios/admin/studio/editStudio',
    DELETE: (id: string) => `/api/v1/studios/admin/deleteStudio/${id}`,
    TOGGLE_ADMIN_PANEL_ACCESS: (id: string) => `/api/v1/studios/admin/toggleAdminPanelAccess/${id}`,
    EVENTS: (id: string) => `/api/v1/events/admin/getStudioEvents/${id}`,
    SESSIONS: (id: string) => `/api/v1/sessions/admin/getStudioSessions/${id}`,
    REVENUE: (id: string) => `/api/v1/studios/admin/revenue/${id}`,
    REVENUE_STUDIO: '/api/v1/studios/admin/studio/revenue',
    ALL_REVENUE: '/api/v1/studios/admin/revenue',
    REVENUE_DETAILS: (id: string) => `/api/v1/studios/admin/revenue/${id}/details`,
    REVENUE_DETAILS_STUDIO: '/api/v1/studios/admin/studio/revenue/details',
    // The other studios of this studio's brand. The three WRITE routes that
    // used to sit here (create / setActive / delete, all keyed on a PARENT
    // STUDIO id) are gone: a branch belongs to a brand now, so creating one
    // lives under API_ENDPOINTS.BRANDS below.
    BRANCHES: (id: string) => `/api/v1/studios/${id}/branches`,
    ORG_OVERVIEW: `/api/v1/dashboard/orgOverview`,
    EARNINGS_SUMMARY: `/api/v1/dashboard/earningsSummary`,
    BOOKINGS_TREND: `/api/v1/dashboard/bookingsTrend`,
    SS_BOOKINGS_COUNT: `/api/v1/dashboard/sweatSocialBookingsCount`,
    STUDIO_ANALYTICS: `/api/v1/dashboard/studioAnalytics`,
    STUDIO_ANALYTICS_SEGMENT: `/api/v1/dashboard/studioAnalyticsSegment`,
    STUDIO_ANALYTICS_PACK_REVENUE: `/api/v1/dashboard/studioAnalyticsPackRevenue`,
  },

  /**
   * Brands — the thing six studios have in common.
   *
   * Everything under /admin is super-admin only: a brand IS the tenancy
   * boundary, so a bug in a self-service version of these routes is a
   * cross-tenant breach rather than a bad edit.
   */
  MULTI_LOCATION: {
    /** Create a class at several locations, or import classes from another location. */
    CLASSES: `/api/v1/multi-location/classes`,
    /** Create sessions at several locations, or import a schedule. */
    SESSIONS: `/api/v1/multi-location/sessions`,
  },
  BRANDS: {
    /** The caller's own brand + the branches they hold. Drives the switcher. */
    MY: `/api/v1/brands/my`,
    LIST: `/api/v1/brands/admin`,
    CREATE: `/api/v1/brands/admin`,
    DETAIL: (brandId: string) => `/api/v1/brands/admin/${brandId}`,
    UPDATE: (brandId: string) => `/api/v1/brands/admin/${brandId}`,
    CREATE_BRANCH: (brandId: string) => `/api/v1/brands/admin/${brandId}/branches`,
    STUDIO_BRAND_LOCATIONS: (studioId: string) =>
      `/api/v1/brands/studios/${studioId}/locations`,
    INSTRUCTOR_LOCATIONS: (instructorId: string) =>
      `/api/v1/brands/instructors/${instructorId}/locations`,
    BRANCH_LOGIN: (brandId: string, branchId: string) =>
      `/api/v1/brands/admin/${brandId}/branches/${branchId}/login`,
    SET_BRANCH_ACTIVE: (brandId: string, branchId: string) =>
      `/api/v1/brands/admin/${brandId}/branches/${branchId}/active`,
    /** Studios that could join this brand (not already a multi-location branch). */
    /** Everyone who could be granted access — feeds the grant picker. */
    PEOPLE: (brandId: string) => `/api/v1/brands/admin/${brandId}/people`,
    ADOPTABLE: (brandId: string) => `/api/v1/brands/admin/${brandId}/adoptable`,
    ADOPT_STUDIO: (brandId: string, studioId: string) =>
      `/api/v1/brands/admin/${brandId}/studios/${studioId}`,
    DETACH_STUDIO: (brandId: string, studioId: string) =>
      `/api/v1/brands/admin/${brandId}/studios/${studioId}`,
    GRANTS: (brandId: string) => `/api/v1/brands/admin/${brandId}/grants`,
    /** Replace the set of locations someone holds at one role. */
    GRANT_LOCATIONS: (brandId: string) => `/api/v1/brands/admin/${brandId}/grants/locations`,
    REVOKE_GRANT: (brandId: string, grantId: string) =>
      `/api/v1/brands/admin/${brandId}/grants/${grantId}`,
  },
  
  // Refunds — the ledger behind a cancelled booking's money.
  // Studio callers hit the /studio/* variants, which scope to their own studio
  // server-side; a studio may never action another studio's refund.
  REFUNDS: {
    LIST: '/api/v1/refunds/admin',
    LIST_STUDIO: '/api/v1/refunds/studio',
    PROCESS: (id: string) => `/api/v1/refunds/admin/${id}/process`,
    PROCESS_STUDIO: (id: string) => `/api/v1/refunds/studio/${id}/process`,
    REJECT: (id: string) => `/api/v1/refunds/admin/${id}/reject`,
    REJECT_STUDIO: (id: string) => `/api/v1/refunds/studio/${id}/reject`,
  },

  // Bookings
  BOOKINGS: {
    LIST: '/api/v1/bookings/getAllBookings',
    STUDIO_CUSTOMERS: '/api/v1/bookings/studio/customers',
    LIST_STUDIO: '/api/v1/bookings/admin/studio/bookings',
    DETAIL: (id: string) => `/api/v1/bookings/admin/getBookingById/${id}`,
    DETAIL_STUDIO: (id: string) => `/api/v1/bookings/admin/studio/booking/${id}`,
    UPDATE: (id: string) => `/api/v1/bookings/${id}`,
    CANCEL: (id: string) => `/api/v1/bookings/cancelBooking/${id}`,
    CANCEL_ADMIN: (id: string) => `/api/v1/bookings/admin/studio/cancelBooking/${id}`,
    DELETE: (id: string) => `/api/v1/bookings/admin/deleteBooking/${id}`,
    UPDATE_STATUS: (id: string) => `/api/v1/bookings/admin/studio/booking/${id}/status`,
    EXPORT_CSV: '/api/v1/bookings/admin/exportBookingsCSV',
    MANUAL_CHECK_USER: '/api/v1/bookings/admin/studio/checkUser',
    MANUAL_CREATE_USER: '/api/v1/bookings/admin/studio/createUser',
    MANUAL_GET_SESSIONS: '/api/v1/bookings/admin/studio/getSessionsForManualBooking',
    MANUAL_CREATE_BOOKING: '/api/v1/bookings/admin/studio/createManualBooking',
    ADMIN_WALK_IN: '/api/v1/bookings/admin/studio/createAdminWalkIn',
    // Bulk pack booking: one member, many classes, one pack.
    BULK_PACK_OPTIONS: '/api/v1/bookings/admin/studio/bulkPackBooking/options',
    BULK_PACK_PREVIEW: '/api/v1/bookings/admin/studio/bulkPackBooking/preview',
    BULK_PACK_CREATE: '/api/v1/bookings/admin/studio/bulkPackBooking',
    BULK_PACK_BOOKINGS: '/api/v1/bookings/admin/studio/bulkPackBooking/bookings',
    BULK_PACK_CANCEL: '/api/v1/bookings/admin/studio/bulkPackBooking/cancel',
    SESSION_WAITLISTS: '/api/v1/bookings/admin/studio/session-waitlists',
    // Studio-managed waitlist (Session Waitlist page)
    WAITLIST_CHECK_ADD: (sessionId: string) => `/api/v1/bookings/admin/studio/waitlist/${sessionId}/check-add`,
    WAITLIST_ADD: (sessionId: string) => `/api/v1/bookings/admin/studio/waitlist/${sessionId}/add`,
    WAITLIST_REORDER: (sessionId: string) => `/api/v1/bookings/admin/studio/waitlist/${sessionId}/reorder`,
    WAITLIST_REMOVE: (entryId: string) => `/api/v1/bookings/admin/studio/waitlist/entry/${entryId}`,
    WAITLIST_BOOK: (entryId: string) => `/api/v1/bookings/admin/studio/waitlist/entry/${entryId}/book`,
    // Staff reschedule — same route for admin and studio; the guard pins a
    // studio caller to its own bookings.
    RESCHEDULE_OPTIONS: (bookingId: string) => `/api/v1/bookings/admin/reschedule-options/${bookingId}`,
    RESCHEDULE: (bookingId: string) => `/api/v1/bookings/admin/reschedule/${bookingId}`,
  },
  
  // Classes
  CLASSES: {
    LIST: '/api/v1/classes/admin/getAllClasses',
    LIST_STUDIO: '/api/v1/classes/admin/studio/classes',
    LIST_BY_STUDIO: (studioId: string) => `/api/v1/classes/admin/getStudioClasses/${studioId}`,
    ALL_TYPES: '/api/v1/classes/getAllClassTypes',
    CREATE_TYPE: '/api/v1/classes/createClassType',
    UPDATE_TYPE: '/api/v1/classes/updateClassType',
    DELETE_TYPE: (id: string) => `/api/v1/classes/deleteClassType/${id}`,
    DETAIL: (id: string) => `/api/v1/classes/admin/getClassById/${id}`,
    DETAIL_STUDIO: (id: string) => `/api/v1/classes/admin/studio/class/${id}`,
    CREATE: (studioId: string) => `/api/v1/classes/admin/createClass/${studioId}`,
    CREATE_STUDIO: '/api/v1/classes/createClass',
    UPDATE: (id: string) => `/api/v1/classes/admin/updateClass/${id}`,
    UPDATE_STUDIO: (id: string) => `/api/v1/classes/admin/studio/updateClass/${id}`,
    UPDATE_IMPACT: (id: string) => `/api/v1/classes/admin/studio/updateImpact/${id}`,
    DELETE: (id: string) => `/api/v1/classes/admin/deleteClass/${id}`,
    DELETE_STUDIO: (id: string) => `/api/v1/classes/deleteClass/${id}`,
  },
  
  // Events
  EVENTS: {
    LIST: '/api/v1/events/admin/getAllEvents',
    LIST_STUDIO: '/api/v1/events/admin/studio/events',
    DETAIL: (id: string) => `/api/v1/events/getEventById/${id}`,
    DETAIL_STUDIO: (id: string) => `/api/v1/events/admin/studio/event/${id}`,
    CREATE: (studioId: string) => `/api/v1/events/admin/createEvent/${studioId}`,
    CREATE_STUDIO: '/api/v1/events/createEvent',
    UPDATE: '/api/v1/events/admin/updateEvent',
    UPDATE_STUDIO: '/api/v1/events/admin/studio/updateEvent',
    DELETE: (id: string) => `/api/v1/events/admin/deleteEvent/${id}`,
    DELETE_STUDIO: (id: string) => `/api/v1/events/deleteEvent/${id}`,
    SHARE_LINKS: (id: string) => `/api/v1/events/admin/studio/shareLinks/${id}`,
  },

  // Merchandise
  SEAT_LAYOUTS: {
    LIST_STUDIO: '/api/v1/seat-layouts',
    LIST_ADMIN: (studioId: string) => `/api/v1/seat-layouts/admin/studio/${studioId}`,
    CREATE_STUDIO: '/api/v1/seat-layouts',
    CREATE_ADMIN: (studioId: string) => `/api/v1/seat-layouts/admin/studio/${studioId}`,
    DETAIL_STUDIO: (id: string) => `/api/v1/seat-layouts/${id}`,
    DETAIL_ADMIN: (id: string) => `/api/v1/seat-layouts/admin/${id}`,
    UPDATE_STUDIO: (id: string) => `/api/v1/seat-layouts/${id}`,
    UPDATE_ADMIN: (id: string) => `/api/v1/seat-layouts/admin/${id}`,
    DELETE_STUDIO: (id: string) => `/api/v1/seat-layouts/${id}`,
    DELETE_ADMIN: (id: string) => `/api/v1/seat-layouts/admin/${id}`,
    DUPLICATE_STUDIO: (id: string) => `/api/v1/seat-layouts/${id}/duplicate`,
    DUPLICATE_ADMIN: (id: string) => `/api/v1/seat-layouts/admin/${id}/duplicate`,
  },

  // Messaging. Studio roles are pinned by JWT; admins pass ?studioId=.
  MESSAGING: {
    OVERVIEW: '/api/v1/messaging/overview',
    CHANNELS: '/api/v1/messaging/channels',
    CREDENTIALS: '/api/v1/messaging/channels/credentials',
    DISCONNECT: '/api/v1/messaging/channels/disconnect',
    VERIFY: '/api/v1/messaging/channels/verify',
    TEMPLATES: '/api/v1/messaging/templates',
    PROVIDER_TEMPLATES: '/api/v1/messaging/templates/provider',
    TEMPLATES_SYNC: '/api/v1/messaging/templates/sync',
    TEMPLATES_SUBMIT: '/api/v1/messaging/templates/submit',
    TEMPLATES_CREATE: '/api/v1/messaging/templates/create',
    TEMPLATE_DELETE: (id: string) => `/api/v1/messaging/templates/${id}`,
    NOTIFICATION_TYPES: '/api/v1/messaging/notification-types',
    MESSAGES: '/api/v1/messaging/messages',
    MESSAGE_DETAIL: (id: string) => `/api/v1/messaging/messages/${id}`,
    LOOKUP: (userId: string) => `/api/v1/messaging/messages/lookup/${userId}`,
    TEST_SEND: '/api/v1/messaging/test-send',
    TEST_PIPELINE: '/api/v1/messaging/test-pipeline',
    MEMBERS: '/api/v1/messaging/members',
    MEMBERS_SUMMARY: '/api/v1/messaging/members/summary',
    INBOUND: '/api/v1/messaging/inbound',
    WORKFLOWS: '/api/v1/messaging/workflows',
    WORKFLOWS_META: '/api/v1/messaging/workflows/meta',
    WORKFLOWS_PREVIEW: '/api/v1/messaging/workflows/preview',
    WORKFLOW: (id: string) => `/api/v1/messaging/workflows/${id}`,
    WORKFLOW_ACTION: (id: string, action: 'cancel' | 'pause' | 'resume' | 'run-now') =>
      `/api/v1/messaging/workflows/${id}/${action}`,
    AUTOMATIONS: '/api/v1/messaging/automations',
    AUTOMATION: (key: string) => `/api/v1/messaging/automations/${encodeURIComponent(key)}`,
    INBOUND_REPLY: (id: string) => `/api/v1/messaging/inbound/${id}/reply`,
    SEND_TO_MEMBER: '/api/v1/messaging/send-to-member',
    CONSENT: (userId: string) => `/api/v1/messaging/consent/${userId}`,
    CONSENT_OPT_OUT: (userId: string) => `/api/v1/messaging/consent/${userId}/opt-out`,
    CONSENT_OPT_IN: (userId: string) => `/api/v1/messaging/consent/${userId}/opt-in`,
    SUPPRESSIONS: '/api/v1/messaging/suppressions',
    SUPPRESSIONS_REMOVE: '/api/v1/messaging/suppressions/remove',
    HEALTH: '/api/v1/messaging/health',
    // Super-admin ops
    ADMIN_PROVIDERS: '/api/v1/admin/messaging/providers/health',
    ADMIN_QUEUES: '/api/v1/admin/messaging/queues',
    ADMIN_CHANNELS: '/api/v1/admin/messaging/channels',
    ADMIN_MATRIX: '/api/v1/admin/messaging/templates/matrix',
    ADMIN_TEMPLATE_STATUS: (id: string) =>
      `/api/v1/admin/messaging/templates/${id}/status`,
    ADMIN_KILL_SWITCH: '/api/v1/admin/messaging/kill-switch',
    ADMIN_COSTS: '/api/v1/admin/messaging/costs',
    ADMIN_FAILURES: '/api/v1/admin/messaging/failures',
  },

  SESSION_SEATMAP: (id: string) => `/api/v1/sessions/${id}/seatmap`,
  // Staff view — same map plus occupant names. Separate route, AdminOrStudio.
  SESSION_SEATMAP_STAFF: (id: string) =>
    `/api/v1/sessions/admin/studio/session/${id}/seatmap`,

  MERCHANDISE: {
    LIST: '/api/v1/merchandise/admin/all',
    LIST_STUDIO: '/api/v1/merchandise/admin/studio/items',
    CREATE: (studioId: string) => `/api/v1/merchandise/admin/create/${studioId}`,
    CREATE_STUDIO: '/api/v1/merchandise/admin/studio/create',
    UPDATE: '/api/v1/merchandise/admin/update',
    UPDATE_STUDIO: '/api/v1/merchandise/admin/studio/update',
    DELETE: (id: string) => `/api/v1/merchandise/admin/delete/${id}`,
    DELETE_STUDIO: (id: string) => `/api/v1/merchandise/admin/studio/delete/${id}`,
    ENQUIRIES: '/api/v1/merchandise/admin/studio/enquiries',

    // ── Inventory ──
    ADJUST_STOCK: (id: string) => `/api/v1/merchandise/admin/studio/items/${id}/stock`,
    STOCK_LEDGER: (id: string) => `/api/v1/merchandise/admin/studio/items/${id}/stock-ledger`,
    LOW_STOCK: '/api/v1/merchandise/admin/studio/low-stock',

    // ── Orders ──
    ORDERS: '/api/v1/merchandise/admin/studio/orders',
    ORDER_STATUS: (id: string) => `/api/v1/merchandise/admin/studio/orders/${id}/status`,
    ORDER_REFUND: (id: string) => `/api/v1/merchandise/admin/studio/orders/${id}/refund`,

    // ── Reporting ──
    REVENUE: '/api/v1/merchandise/admin/studio/revenue',
  },

  // Sessions
  SESSIONS: {
    LIST: '/api/v1/sessions',
    LIST_STUDIO: '/api/v1/sessions/admin/studio/sessions',
    DETAIL: (id: string) => `/api/v1/sessions/getSessionById/${id}`,
    DETAIL_STUDIO: (id: string) => `/api/v1/sessions/admin/studio/session/${id}`,
    CREATE: (studioId: string) => `/api/v1/sessions/admin/createSession/${studioId}`,
    CREATE_STUDIO: '/api/v1/sessions/createSession',
    CREATE_MULTIPLE: (studioId: string) => `/api/v1/sessions/admin/createMultipleSessions/${studioId}`,
    CREATE_MULTIPLE_STUDIO: '/api/v1/sessions/createMultipleSessions',
    UPDATE: (id: string) => `/api/v1/sessions/admin/updateSession/${id}`,
    UPDATE_STUDIO: (id: string) => `/api/v1/sessions/updateSession/${id}`,
    DELETE: (id: string) => `/api/v1/sessions/admin/deleteSession/${id}`,
    DELETE_STUDIO: (id: string) => `/api/v1/sessions/deleteSession/${id}`,
    DELETE_BY_DATE: '/api/v1/sessions/admin/deleteSessionsByDate',
    ACTIVE_BY_DATE: '/api/v1/sessions/admin/getActiveSessionsByDate',
    ALL_BY_DATE: '/api/v1/sessions/admin/getAllSessionsByDate',
    BULK_CREATE_BY_RANGE: (studioId: string) => `/api/v1/sessions/admin/bulkCreateByRange/${studioId}`,
    BULK_CREATE_BY_RANGE_STUDIO: '/api/v1/sessions/bulkCreateByRange',
    BULK_DELETE_BY_RANGE: (studioId: string) => `/api/v1/sessions/admin/bulkDeleteByRange/${studioId}`,
    BULK_DELETE_BY_RANGE_STUDIO: '/api/v1/sessions/bulkDeleteByRange',
    // Bulk workspace: preview is read-only and always runs before an edit/delete.
    BULK_PREVIEW_BY_RANGE: (studioId: string) => `/api/v1/sessions/admin/bulkPreviewByRange/${studioId}`,
    BULK_PREVIEW_BY_RANGE_STUDIO: '/api/v1/sessions/bulkPreviewByRange',
    BULK_UPDATE_BY_RANGE: (studioId: string) => `/api/v1/sessions/admin/bulkUpdateByRange/${studioId}`,
    BULK_UPDATE_BY_RANGE_STUDIO: '/api/v1/sessions/bulkUpdateByRange',
  },

  // Instructors
  INSTRUCTORS: {
    LIST: (studioId: string) => `/api/v1/instructor/admin/getAllInstructors/${studioId}`,
    // Super admins have no studio on their token; this is the cross-studio list.
    LIST_ALL: '/api/v1/instructor/admin/getAllInstructors',
    LIST_STUDIO: '/api/v1/instructor/getAllInstructors',
    DETAIL: (id: string) => `/api/v1/instructor/getInstructorById/${id}`,
    CREATE: (studioId: string) => `/api/v1/instructor/admin/createInstructor/${studioId}`,
    CREATE_STUDIO: '/api/v1/instructor/createInstructor',
    UPDATE: (id: string) => `/api/v1/instructor/admin/updateInstructor/${id}`,
    UPDATE_STUDIO: '/api/v1/instructor/updateInstructor',
    DELETE: (id: string) => `/api/v1/instructor/admin/deleteInstructor/${id}`,
    DELETE_STUDIO: (id: string) => `/api/v1/instructor/deleteInstructor/${id}`,
    SET_DISABLED: (id: string) => `/api/v1/instructor/admin/setInstructorDisabled/${id}`,
    SET_DISABLED_STUDIO: (id: string) => `/api/v1/instructor/setInstructorDisabled/${id}`,
    FUTURE_WORK: (id: string) => `/api/v1/instructor/admin/futureAssignments/${id}`,
    FUTURE_WORK_STUDIO: (id: string) => `/api/v1/instructor/futureAssignments/${id}`,
    // Performance. One route for both admin and studio — the guard pins a
    // studio caller to its own instructors, so there is no `_STUDIO` variant.
    PERFORMANCE: (id: string) => `/api/v1/instructor/${id}/performance`,
    PERFORMANCE_SUMMARY: '/api/v1/instructor/performance/summary',
  },
  
  // Transactions
  TRANSACTIONS: {
    LIST: '/api/v1/transactions/admin/getAllTransactions',
    LIST_STUDIO: '/api/v1/transactions/admin/studio/getStudioTransactions',
    DETAIL: (id: string) => `/api/v1/transactions/getTransactionById/${id}`,
    STATS: '/api/v1/transactions/admin/getTransactionStats',
    STATS_STUDIO: '/api/v1/transactions/admin/studio/getStudioTransactionStats',
    STATS_SEGMENT: '/api/v1/transactions/admin/getStatsSegmentTransactions',
    STATS_SEGMENT_STUDIO: '/api/v1/transactions/admin/studio/getStudioStatsSegmentTransactions',
  },
  
  // Reviews
  REVIEWS: {
    LIST: '/api/v1/reviews/admin/all',
    // Studio owners and managers. Their studioId comes from the token — a
    // studioId in the query is ignored for them server-side.
    LIST_STUDIO: '/api/v1/reviews/studio/all',
    DETAIL: (id: string) => `/api/v1/reviews/admin/${id}`,
    UPDATE: (id: string) => `/api/v1/reviews/admin/${id}`,
    DELETE: (id: string) => `/api/v1/reviews/admin/${id}`,
  },
  
  // Packs
  PACKS: {
    LIST: '/api/v1/packs/admin/getAllPacks',
    LIST_STUDIO: '/api/v1/packs/studio/getAllStudioPacks',
    DETAIL: (id: string) => `/api/v1/packs/admin/getPackById/${id}`,
    DETAIL_STUDIO: (id: string) => `/api/v1/packs/studio/getStudioPackById/${id}`,
    CREATE: '/api/v1/packs/admin/createPack',
    CREATE_STUDIO: '/api/v1/packs/studio/createStudioPack',
    UPDATE: '/api/v1/packs/admin/updatePack',
    UPDATE_STUDIO: '/api/v1/packs/studio/updateStudioPack',
    HOLDERS: (id: string) => `/api/v1/packs/admin/packHolders/${id}`,
    HOLDERS_STUDIO: (id: string) => `/api/v1/packs/studio/packHolders/${id}`,
    DELETE: (id: string) => `/api/v1/packs/admin/deletePack/${id}`,
    DELETE_STUDIO: (id: string) => `/api/v1/packs/studio/deleteStudioPack/${id}`,
    TOGGLE_STATUS: (id: string) => `/api/v1/packs/admin/togglePackStatus/${id}`,
    TOGGLE_STATUS_STUDIO: (id: string) => `/api/v1/packs/studio/toggleStudioPackStatus/${id}`,
    PURCHASED_LIST: '/api/v1/packs/admin/getAllPurchasedPacks',
    PURCHASED_LIST_STUDIO: '/api/v1/packs/studio/getPurchasedPacks',
    PURCHASED_DETAIL: (id: string) => `/api/v1/packs/admin/getPurchasedPackById/${id}`,
    PURCHASED_STATS: '/api/v1/packs/admin/getPurchasedPacksStats',
    PURCHASED_STATS_STUDIO: '/api/v1/packs/studio/getPurchasedPacksStats',
    PURCHASED_EDIT: (id: string) => `/api/v1/packs/admin/editPurchasedPack/${id}`,
    PURCHASED_EDIT_STUDIO: (id: string) => `/api/v1/packs/studio/editPurchasedPack/${id}`,
    PURCHASED_DELETE: (id: string) => `/api/v1/packs/admin/studio/deletePurchasedPack/${id}`,
    MANUAL_CHECK_USER: '/api/v1/packs/admin/studio/checkUser',
    MANUAL_CREATE_USER: '/api/v1/packs/admin/studio/createUserForPack',
    MANUAL_ASSIGN: '/api/v1/packs/admin/studio/assignPack',
    PURCHASED_HISTORY: (id: string) => `/api/v1/packs/admin/${id}/history`,
    FREEZE: (id: string) => `/api/v1/packs/admin/freeze/${id}`,
    UNFREEZE: (id: string) => `/api/v1/packs/admin/unfreeze/${id}`,
    FREEZE_HISTORY_DELETE: (packId: string, entryId: string) => `/api/v1/packs/admin/${packId}/freeze-history/${entryId}`,
  },
  
  // Promo Codes
  PROMO_CODES: {
    LIST: '/api/v1/promo-code/admin/getAllPromoCodes',
    DETAIL: (id: string) => `/api/v1/promo-code/getPromoCodeById/${id}`,
    CREATE: '/api/v1/promo-code/admin/createPromoCode',
    UPDATE: (id: string) => `/api/v1/promo-code/admin/updatePromoCode`,
    DELETE: (id: string) => `/api/v1/promo-code/admin/deletePromoCode/${id}`,
  },
  
  // Coupons
  COUPONS: {
    LIST: '/api/v1/coupons/admin/getAllCoupons',
    LIST_STUDIO: '/api/v1/coupons/studio/getAllCoupons',
    DETAIL: (id: string) => `/api/v1/coupons/admin/getCouponById/${id}`,
    DETAIL_STUDIO: (id: string) => `/api/v1/coupons/studio/getCouponById/${id}`,
    CREATE: '/api/v1/coupons/admin/createCoupon',
    CREATE_STUDIO: '/api/v1/coupons/studio/createCoupon',
    UPDATE: '/api/v1/coupons/admin/updateCoupon',
    UPDATE_STUDIO: '/api/v1/coupons/studio/updateCoupon',
    DELETE: (id: string) => `/api/v1/coupons/admin/deleteCoupon/${id}`,
    DELETE_STUDIO: (id: string) => `/api/v1/coupons/studio/deleteCoupon/${id}`,
    VALIDATE: '/api/v1/coupons/validateCoupon',
  },
  
  // Advertisements
  ADVERTISEMENTS: {
    // Super-admin endpoints
    LIST: '/api/v1/advertisements/admin/getAllAdvertisements',
    DETAIL: (id: string) => `/api/v1/advertisements/admin/getAdvertisementById/${id}`,
    CREATE: '/api/v1/advertisements/admin/createAdvertisement',
    UPDATE: '/api/v1/advertisements/admin/updateAdvertisement',
    DELETE: (id: string) => `/api/v1/advertisements/admin/deleteAdvertisement/${id}`,
    TOGGLE_STATUS: (id: string) => `/api/v1/advertisements/admin/toggleAdvertisementStatus/${id}`,
    // Studio-owner / manager endpoints (StudioGuard — scoped to the requesting studio)
    STUDIO_LIST: '/api/v1/advertisements/studio/getAllAdvertisements',
    STUDIO_DETAIL: (id: string) => `/api/v1/advertisements/getAdvertisementById/${id}`,
    STUDIO_CREATE: '/api/v1/advertisements/createAdvertisement',
    STUDIO_UPDATE: '/api/v1/advertisements/updateAdvertisement',
    STUDIO_DELETE: (id: string) => `/api/v1/advertisements/deleteAdvertisement/${id}`,
    STUDIO_TOGGLE_STATUS: (id: string) => `/api/v1/advertisements/studio/toggleAdvertisementStatus/${id}`,
  },
  
  // Notifications
  NOTIFICATIONS: {
    SEND_LEGACY: '/api/v1/notifications/send/push-notifications',
    // Admin (super admin only)
    CAMPAIGNS: '/api/v1/notifications/admin/campaigns',
    CAMPAIGN_DETAIL: (id: string) => `/api/v1/notifications/admin/campaigns/${id}`,
    CAMPAIGN_DELETE: (id: string) => `/api/v1/notifications/admin/campaigns/${id}`,
    CAMPAIGN_TRACK_OPEN: (id: string) => `/api/v1/notifications/admin/campaigns/${id}/track-open`,
    CAMPAIGN_CANCEL_SCHEDULE: (id: string) => `/api/v1/notifications/admin/campaigns/${id}/cancel-schedule`,
    SEGMENT_PREVIEW: '/api/v1/notifications/admin/segments/preview',
    TEMPLATES: '/api/v1/notifications/admin/templates',
    LEADS: '/api/v1/notifications/admin/leads',
    LEAD_EMAIL_REMINDER: (id: string) => `/api/v1/notifications/admin/leads/${id}/send-email-reminder`,
    // Studio (scoped to studio's own users only)
    STUDIO_CAMPAIGNS: '/api/v1/notifications/studio/campaigns',
    STUDIO_CAMPAIGN_DELETE: (id: string) => `/api/v1/notifications/studio/campaigns/${id}`,
    STUDIO_SEGMENT_PREVIEW: '/api/v1/notifications/studio/segments/preview',
  },
  
  // Common
  COMMON: {
    UPLOAD_FILE: '/api/v1/common/uploadFile',
    UPLOAD_FILES: '/api/v1/common/admin/uploadFiles',
    WEBSITE_INQUIRIES: '/api/v1/common/admin/website/inquiries',
    STUDIO_CONTACT_INQUIRIES: '/api/v1/common/admin/website/studio-contacts',
    CORPORATE_LEADS: '/api/v1/common/admin/corporate-leads',
  },
  
  // Leads
  LEADS: {
    LIST: '/api/v1/leads/getAllLeads',
  },
  
  // Waitlist
  WAITLIST: {
    LIST: '/api/v1/waitlist/admin/entries',
    STATS: '/api/v1/waitlist/admin/stats',
  },

  // Studio Users (Staff)
  STUDIO_USERS: {
    ADMIN_CREATE: '/api/v1/studio-users/admin/create',
    ADMIN_LIST: (studioId: string) => `/api/v1/studio-users/admin/list?studioId=${studioId}`,
    ADMIN_UPDATE: (id: string) => `/api/v1/studio-users/admin/${id}`,
    ADMIN_DELETE: (id: string) => `/api/v1/studio-users/admin/${id}`,
    STUDIO_CREATE: '/api/v1/studio-users/studio/create',
    STUDIO_LIST: '/api/v1/studio-users/studio/list',
    STUDIO_UPDATE: (id: string) => `/api/v1/studio-users/studio/${id}`,
    STUDIO_DELETE: (id: string) => `/api/v1/studio-users/studio/${id}`,
    RESET_PASSWORD: '/api/v1/studio-users/reset-password',
  },

  // Studio Enquiries (from website "Get Started" form)
  STUDIO_ENQUIRY: {
    LIST: '/api/v1/studio-enquiry/admin/list',
    UPDATE: (id: string) => `/api/v1/studio-enquiry/admin/${id}`,
  },

  // Partner / Whitelabel
  PARTNER: {
    GENERATE_API_KEY: (studioId: string) => `/api/v1/studios/admin/partner/generateApiKey/${studioId}`,
    REVOKE_API_KEY: (studioId: string) => `/api/v1/studios/admin/partner/revokeApiKey/${studioId}`,
    LIST_PARTNER_STUDIOS: '/api/v1/studios/admin/partner/studios',
    API_LOGS: '/api/v1/partner/admin/logs',
    API_STATS: '/api/v1/partner/admin/stats',
  },

  // Audit Logs
  AUDIT_LOGS: {
    LIST: '/api/v1/audit-logs',
    LIST_STUDIO: '/api/v1/audit-logs/studio',
  },
  // Studio Payments (Weekly Statements)
  STUDIO_PAYMENTS: {
    GENERATE: '/api/v1/admin/studio-payments/generate',
    LIST: '/api/v1/admin/studio-payments',
    MRR: '/api/v1/admin/studio-payments/mrr',
    DETAILS: (id: string) => `/api/v1/admin/studio-payments/${id}/details`,
    MARK_PAID: (id: string) => `/api/v1/admin/studio-payments/${id}/mark-paid`,
    ADJUSTMENT: (id: string) => `/api/v1/admin/studio-payments/${id}/adjustment`,
    SEND_INVOICE: (id: string) => `/api/v1/admin/studio-payments/${id}/send-invoice`,
    DELETE: (id: string) => `/api/v1/admin/studio-payments/${id}`,
  },

  // Referrals — member-get-member. Studio-scoped: the backend resolves the
  // studio from the caller's token, so no studioId travels in these paths.
  REFERRALS: {
    PROGRAM: '/api/v1/referrals/admin/program',
    PROGRAM_ACTIVE: '/api/v1/referrals/admin/program/active',
    REPORT: '/api/v1/referrals/admin/report',
    LEDGER: '/api/v1/referrals/admin/ledger',
    GRANT: '/api/v1/referrals/admin/grant',
    REVOKE: (id: string) => `/api/v1/referrals/admin/benefit/${id}/revoke`,
  },

  // Visit milestones — same tenancy rules as referrals.
  MILESTONES: {
    PROGRAM: '/api/v1/milestones/admin/program',
    PROGRAM_ACTIVE: '/api/v1/milestones/admin/program/active',
    AWARDS: '/api/v1/milestones/admin/awards',
    FULFIL: (id: string) => `/api/v1/milestones/admin/awards/${id}/fulfil`,
    REVOKE: (id: string) => `/api/v1/milestones/admin/awards/${id}/revoke`,
  },

  // Studio-facing payouts (studio owner / partner app — StudioGuard, own studio only)
  STUDIO_PAYOUTS: {
    LIST: '/api/v1/studio/payouts',
    SUMMARY: '/api/v1/studio/payouts/summary',
    DETAILS: (id: string) => `/api/v1/studio/payouts/${id}`,
    RECEIPT: (id: string) => `/api/v1/studio/payouts/${id}/receipt`,
  },

  QA: {
    TEST_CASES: '/api/v1/admin/qa/test-cases',
    CATEGORIES: '/api/v1/admin/qa/categories',
    HEALTH: '/api/v1/admin/qa/health',
    FCM_TOKENS: (phone: string) => `/api/v1/admin/qa/fcm-tokens?phone=${encodeURIComponent(phone)}`,
    RESOLVE_TOKEN: '/api/v1/admin/qa/resolve-token',
    TEST_NOTIFICATION: '/api/v1/admin/qa/test-notification',
    RUN_TEST: (runId: string) => `/api/v1/admin/qa/run-test/${runId}`,
  },

  // Invoice Generator
  INVOICES: {
    LIST: '/api/v1/invoices',
    CREATE: '/api/v1/invoices',
    NEXT_NUMBER: '/api/v1/invoices/next-number',
    REVENUE_SUMMARY: '/api/v1/invoices/revenue-summary',
    DETAIL: (id: string) => `/api/v1/invoices/${id}`,
    UPDATE: (id: string) => `/api/v1/invoices/${id}`,
    DELETE: (id: string) => `/api/v1/invoices/${id}`,
  },

  // Feature Requests — studio owner side (StudioAdminOnlyGuard, scoped to caller's studio)
  FEATURE_REQUESTS: {
    LIST: '/api/v1/feature-requests',
    CREATE: '/api/v1/feature-requests',
    SIMILAR: (q: string) => `/api/v1/feature-requests/similar?q=${encodeURIComponent(q)}`,
    DETAIL: (id: string) => `/api/v1/feature-requests/${id}`,
    VOTE: (id: string) => `/api/v1/feature-requests/${id}/vote`,
    COMMENTS: (id: string) => `/api/v1/feature-requests/${id}/comments`,
  },

  // Feature Requests — internal admin side (AdminGuard)
  ADMIN_FEATURE_REQUESTS: {
    LIST: '/api/v1/admin/feature-requests',
    UPDATE: (id: string) => `/api/v1/admin/feature-requests/${id}`,
    SHIP: (id: string) => `/api/v1/admin/feature-requests/${id}/ship`,
    IMPACT: '/api/v1/admin/feature-requests/impact',
    DUPLICATES: '/api/v1/admin/duplicates',
    DUPLICATE_MERGE: (id: string) => `/api/v1/admin/duplicates/${id}/merge`,
    DUPLICATE_DISMISS: (id: string) => `/api/v1/admin/duplicates/${id}/dismiss`,
  },

  // Feature Requests — public changelog (no auth)
  PUBLIC_CHANGELOG: '/api/v1/public/changelog',
};

// Request/Response Types
export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

export interface PaginatedResponse<T = any> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface QueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filter?: Record<string, any>;
}

// Error Types
export interface ApiError {
  message: string;
  status: number;
  code?: string;
  /**
   * The server rejected this but the caller MAY retry with an explicit
   * override (e.g. granting a trial to a member who has already used one).
   * Distinguishes a confirmable warning from a hard failure.
   */
  overridable?: boolean;
  /**
   * The response body verbatim.
   *
   * A refusal is not always just a message: the instructor-removal refusal
   * carries the counts of what still points at them, and the picker is built
   * from those. Copying fields one at a time meant every such payload was
   * silently truncated to whatever someone had remembered to add here — read
   * this instead of adding another field.
   *
   * NOTE: callers must read `err.data`, NOT `err.response.data`. The response
   * interceptor flattens the axios error, so `err.response` does not exist.
   */
  data?: any;
}
